import Row from '@amzn/meridian/row';
import React from 'react';
import DateRangePicker from '@amzn/meridian/date-range-picker';

import Toggle from '@amzn/meridian/toggle';
import Select, { SelectOption } from '@amzn/meridian/select';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  getDefaultDateRange,
  isValidDateRange,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import {
  FILTER_DROPDOWN_CUSTOM_DATE_WIDTH,
  FILTER_DROPDOWN_WIDTH,
} from '../../../../Util/GlobalConstants';
import moment from 'moment';
import useCustomTracking from '../../../../Util/useCustomTracking';

interface CustomDateProps {
  enableCustomDateRange: any;
  customDate: any;
  dateRange: any;
  weekValue: string;
  weekOptions: string[];
  page: string;
  setCustomDate: any;
  setDateRange: any;
  weekValueChanger: any;
}

const CustomDate: React.FC<CustomDateProps> = ({
  enableCustomDateRange,
  customDate,
  dateRange,
  weekValue,
  weekOptions,
  page,
  setCustomDate,
  setDateRange,
  weekValueChanger,
}: CustomDateProps): React.ReactElement => {
  const intl = useIntl();
  const latestBound = new Date();
  const earliestBound = new Date(
    new Date().setFullYear(new Date().getFullYear() - 1)
  );

  const [TrackingTag, triggerClick] = useCustomTracking(
    'team-customdate-selection',
    'team-customdate-selection-slot',
    {
      'data-csa-c-start-date': enableCustomDateRange
        ? moment(dateRange[0]).format('MM/DD/YYYY')
        : undefined,
      'data-csa-c-end-date': enableCustomDateRange
        ? moment(dateRange[1]).format('MM/DD/YYYY')
        : undefined,
      'data-csa-c-tag1': page,
      'data-csa-c-type': 'select',
    }
  );

  const dateRangeSelector = (
    <>
      <TrackingTag />
      <DateRangePicker
        value={dateRange}
        onChange={(values: any) => {
          const newDateRange = [...values];
          setDateRange(newDateRange);
          if (isValidDateRange(dateRange[0], dateRange[1])) {
            triggerClick();
          }
        }}
        disabledDates={(date: any) => {
          const date_ = new Date(date);
          return (
            date_.valueOf() < earliestBound.valueOf() ||
            date_.valueOf() > latestBound.valueOf()
          );
        }}
        allowSameStartAndEnd={true}
        startLabel='Start date'
        endLabel='End date'
        monthsInView={2}
        width={FILTER_DROPDOWN_CUSTOM_DATE_WIDTH}
      />
    </>
  );

  const weeklySelector = (
    <Select
      label='Week'
      value={weekValue}
      onChange={weekValueChanger}
      size='xlarge'
      width={FILTER_DROPDOWN_WIDTH}
      data-cy='week'
    >
      {weekOptions.map((week, index) => {
        return index === 0 ? (
          <SelectOption
            key={week}
            value={week}
            label={intl.formatMessage({
              id: 'cs_perfportal_week_trailing_seven_days',
              defaultMessage: 'Trailing 7 days',
            })}
            data-csa-c-type='select'
            data-csa-c-slot-id='lighthouse-filters-select-field-week-slot'
            data-csa-c-content-id='lighthouse-filters-select-field-week'
            data-csa-c-selected-value='Trailing 7 days'
            data-csa-c-tag1={page}
          />
        ) : (
          <SelectOption
            key={week}
            value={week}
            label={week}
            data-csa-c-type='select'
            data-csa-c-slot-id='lighthouse-filters-select-field-week-slot'
            data-csa-c-content-id='lighthouse-filters-select-field-week'
            data-csa-c-selected-value={week}
            data-csa-c-tag1={page}
          />
        );
      })}
    </Select>
  );

  if (!enableCustomDateRange) {
    // return one single weekly selector
    return weeklySelector;
  } else {
    // return a combined component of a toggle switch + date range selector/weekly selector
    return (
      <Row>
        <Toggle
          checked={customDate}
          onChange={(value: any) => {
            if (!value) {
              setDateRange(getDefaultDateRange());
            }
            setCustomDate(value);
          }}
          size='medium'
          disabled={false}
          data-csa-c-type='toggle'
          data-csa-c-slot-id='team-customdate-toggle-slot'
          data-csa-c-content-id='team-customdate-toggle'
          data-csa-c-tag1={page}
        >
          <FormattedMessage
            id='cs_perfportal_custom_date'
            defaultMessage='Custom Date'
          />
        </Toggle>
        {customDate ? dateRangeSelector : weeklySelector}
      </Row>
    );
  }
};
export default CustomDate;
